/**
 * Quem pode mexer o cartaz, e quantos de cada vez.
 *
 * ## Por que isto é um arquivo, e não três `if` dentro do componente
 *
 * Porque as duas regras que mais importam aqui são invisíveis na tela de quem
 * escreve: ninguém desenvolve com economia de dados ligada, e ninguém desenvolve
 * com `prefers-reduced-motion` ligado. Uma regra que só é exercida por quem tem
 * o problema é uma regra que quebra em silêncio e volta meses depois. Separada,
 * ela tem teste.
 *
 * A segunda razão é o "um de cada vez": ele é global à página, não à prateleira.
 * Cinco cartazes tocando na mesma faixa é o que faz um celular esquentar, e o
 * componente não tem como saber o que os outros estão fazendo. Quem sabe é isto.
 */

/**
 * O cartaz animado é um GIF — e essa não foi a primeira escolha.
 *
 * ## O que se pediu, e o que dá para servir
 *
 * O pedido descrevia um `<video muted loop playsinline preload="none">`, que é o
 * que a Netflix faz. Ela pode: ela transcodifica. Nós não — e os dois
 * fornecedores que temos entregam a prévia curta como **imagem animada**, não
 * como vídeo:
 *
 * - Cloudflare Stream: `thumbnails/thumbnail.gif`. Medido em 08/08/2026,
 *   `thumbnail.webp` responde **404** — GIF é a única saída animada dela.
 * - Bunny Stream: `preview.webp`, também imagem.
 *
 * Servir vídeo de verdade exigiria ou HLS (e `hls.js`, ~130 kB de JavaScript
 * novo, quando a régua V13 já está estourada) ou um MP4 recortado que ninguém
 * nesta instalação sabe produzir. As duas contas são piores que a imagem.
 *
 * ## O que se ganha, e não é consolo
 *
 * Cada regra da tela sai mais forte com imagem do que sairia com `<video>`:
 *
 * | pedido | com `<video>` | aqui |
 * |---|---|---|
 * | mudo | `muted`, que o navegador pode ignorar | **não existe faixa de áudio** |
 * | em laço | `loop` | o GIF é laço por definição |
 * | `playsinline` | atributo, e o iOS às vezes discorda | não há tela cheia a evitar |
 * | `preload="none"` | dica; o navegador decide | **não há `src` até alguém querer** |
 *
 * E custa **zero byte de JavaScript novo**: nenhuma biblioteca, nenhum
 * `<video>`, nenhum gerenciador de mídia.
 *
 * ## O que se perde, dito para não surpreender
 *
 * Um GIF **não pausa**. Parar é tirar o `src` — que é o que `parar()` faz, e é
 * por isso que o cartaz parado precisa estar embaixo: sem ele, tirar o `src`
 * deixaria um buraco. É a mesma razão pela qual a imagem é obrigatória.
 */

/** O que o navegador diz sobre querer movimento. */
export interface AmbienteDoMovimento {
  reduzirMovimento: boolean;
  economiaDeDados: boolean;
}

/**
 * Lê o ambiente. Fora do navegador, responde "não anime".
 *
 * O padrão é o conservador de propósito: um servidor renderizando isto não tem
 * como perguntar, e presumir movimento erraria para o lado que custa bytes.
 */
export function lerAmbienteDoMovimento(): AmbienteDoMovimento {
  if (typeof window === 'undefined' || typeof matchMedia !== 'function') {
    return { reduzirMovimento: true, economiaDeDados: true };
  }
  const conexao = (navigator as { connection?: { saveData?: boolean } }).connection;
  return {
    reduzirMovimento: matchMedia('(prefers-reduced-motion: reduce)').matches,
    economiaDeDados: conexao?.saveData === true,
  };
}

/**
 * Pode animar?
 *
 * ## Por que os dois pesam igual, e nenhum é "quase"
 *
 * `prefers-reduced-motion` é um pedido de acessibilidade: há quem passe mal com
 * movimento repetitivo, e um laço de três segundos numa prateleira de seis é
 * exatamente o padrão que provoca isso. Não há versão suave dele.
 *
 * `saveData` é um pedido de dinheiro. Um cartaz animado custa de 292 a 400 kB
 * contra 20 kB do parado — de 14x a 20x. Quem ligou economia de dados no
 * aparelho está dizendo que paga por byte, e gastar vinte vezes mais para
 * enfeitar um cartaz é a decisão que essa chave existe para impedir.
 *
 * Nos dois casos o resultado é o mesmo e é o certo: **o cartaz parado**, que já
 * está na tela, com o nome do curso embaixo. Não falta nada — o que não acontece
 * é o movimento.
 */
export function podeAnimar(ambiente: AmbienteDoMovimento): boolean {
  return !ambiente.reduzirMovimento && !ambiente.economiaDeDados;
}

/**
 * O maestro: um cartaz animado de cada vez, na página inteira.
 *
 * ## Por que um só
 *
 * Cinco GIFs de 400 kB na mesma faixa são dois megabytes e cinco laços a
 * decodificar por quadro. É o que faz o aparelho esquentar e a rolagem engasgar
 * — e a pessoa está olhando **um** cartaz, sempre. Os outros quatro são custo
 * puro.
 *
 * ## Por que o maestro é do módulo, e não de um contexto do React
 *
 * Porque o escopo dele é a página, não uma árvore de componentes: a vitrine tem
 * prateleiras irmãs que não se conhecem, e um contexto por prateleira permitiria
 * uma animação em cada. E porque um contexto custaria um provedor e um
 * `useContext` em todo cartão, para guardar um identificador.
 */
let tocandoAgora: string | null = null;
const ouvintes = new Map<string, () => void>();

/** Pede a vez. Quem estava tocando é avisado para parar. */
export function pedirAVez(id: string, aoPerder: () => void): void {
  if (tocandoAgora === id) return;
  const anterior = tocandoAgora;
  tocandoAgora = id;
  ouvintes.set(id, aoPerder);
  if (anterior !== null) {
    const parar = ouvintes.get(anterior);
    ouvintes.delete(anterior);
    parar?.();
  }
}

/** Devolve a vez, se ela ainda for sua. */
export function devolverAVez(id: string): void {
  ouvintes.delete(id);
  if (tocandoAgora === id) tocandoAgora = null;
}

/** Só para teste: quem está com a vez agora. */
export function quemEstaTocando(): string | null {
  return tocandoAgora;
}

/** Só para teste: recomeça do zero entre casos. */
export function esquecerAVez(): void {
  tocandoAgora = null;
  ouvintes.clear();
}

/**
 * O endereço da prévia daquele curso.
 *
 * Uma rota nossa que redireciona, e não o endereço do fornecedor: o vídeo pode
 * exigir assinatura, e a assinatura não pode viver num JSON que fica no cache do
 * navegador. Ver a rota, em `aluno.ts`.
 */
export function enderecoDaPrevia(areaSlug: string, cursoId: string): string {
  return `/aluno/api/a/${encodeURIComponent(areaSlug)}/cursos/${encodeURIComponent(cursoId)}/previa`;
}
